import { ApplyProposalsResult, Transcription, TranscriptionProposalItem, TranscriptionProposals, UpdateTranscriptionDto } from '../models/transcription'
import { baseApi, Tag } from './base'
import { cacher } from '@/utils/rtkQueryCacheUtils'

export const TranscriptionApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getTranscription: builder.query<Transcription, string>({
      query: (transcriptionId) => ({
        url: `transcriptions/${transcriptionId}`,
        method: 'GET',
      }),
      providesTags: cacher.cacheByIdArg(Tag.TRANSCRIPTION),
    }),
    updateTranscriptionInfo: builder.mutation<Transcription, { transcriptionId: string; body: UpdateTranscriptionDto }>({
      query: ({ transcriptionId, body }) => ({
        url: `transcriptions/${transcriptionId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { transcriptionId }) => [{ type: Tag.TRANSCRIPTION, id: transcriptionId }],
    }),
    deleteTranscription: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `transcriptions/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: Tag.TRANSCRIPTION, id }],
    }),
    getTranscriptionByConsultation: builder.query<Transcription | null, string>({
      query: (consultationId) => ({
        url: `transcriptions/consultation/${consultationId}`,
        method: 'GET',
      }),
      providesTags: (result, _error, consultationId) => [
        { type: Tag.TRANSCRIPTION, id: `consultation-${consultationId}` },
        ...(result ? [{ type: Tag.TRANSCRIPTION, id: result._id }] : []),
      ],
    }),
    requestTranscriptionConsent: builder.mutation<{ recordingConsentPending: true }, { consultationId: string }>({
      query: ({ consultationId }) => ({
        url: `transcriptions/consultation/${consultationId}/consent/request`,
        method: 'POST',
      }),
    }),
    recordTranscriptionConsent: builder.mutation<{ recordingConsent: boolean }, { consultationId: string; accepted: boolean }>({
      query: ({ consultationId, accepted }) => ({
        url: `transcriptions/consultation/${consultationId}/consent`,
        method: 'POST',
        body: { accepted },
      }),
      invalidatesTags: (_result, _error, { consultationId }) => [{ type: Tag.CONSULTATION, id: consultationId }],
    }),
    proposeStructuredData: builder.mutation<TranscriptionProposals, { transcriptionId: string }>({
      query: ({ transcriptionId }) => ({
        url: `transcriptions/${transcriptionId}/proposals`,
        method: 'POST',
      }),
    }),
    applyStructuredData: builder.mutation<ApplyProposalsResult, { transcriptionId: string; consultationId: string; items: TranscriptionProposalItem[] }>({
      query: ({ transcriptionId, items }) => ({
        url: `transcriptions/${transcriptionId}/proposals/apply`,
        method: 'POST',
        body: { items },
      }),
      invalidatesTags: (_result, _error, { consultationId }) => [
        { type: Tag.CONSULTATION, id: consultationId },
        { type: Tag.CONSULTATION, id: 'LIST' },
        { type: Tag.PATIENT, id: 'LIST' },
      ],
    }),
    transcribeFromFile: builder.mutation<Transcription, { file: File; consultationId: string; userId: string }>({
      query: ({ file, consultationId, userId }) => {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('consultationId', consultationId)
        formData.append('userId', userId)
        return {
          url: 'transcriptions/from-file',
          method: 'POST',
          body: formData,
        }
      },
    }),
  }),
})
